/**
 * TimeEntryMigration — admin utility to copy Airtable time entries into Supabase (Round 15 Phase 2).
 *
 * One-click trigger for the idempotent backfill (api/time-entries-import.js). Re-runnable any number
 * of times (upsert on source_airtable_id) — run it again at cutover to capture the latest. The app
 * still reads/writes Airtable until the service is flipped; this only copies data into Supabase.
 */
import { useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

export default function TimeEntryMigration({ dark }) {
  const [busy, setBusy]     = useState(false)
  const [result, setResult] = useState(null)
  const [err, setErr]       = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function call(method) {
    if (method === 'POST' && !(await confirmDialog({ title: 'Copy time entries to Supabase?', message: 'Copy all Airtable time entries into Supabase now? Safe to re-run — it upserts, no duplicates. (Does not change what the app reads/writes yet.)', confirmLabel: 'Run import' }))) return
    setBusy(true); setErr(null); setResult(null)
    try {
      const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
      const res = await fetch('/api/time-entries-import', {
        method, headers: token ? { Authorization: 'Bearer ' + token } : {},
      })
      const d = await res.json()
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
      setResult(d)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 620 }}>
        Copies every time entry from the Airtable staging table into Supabase (<code>time_entries</code>).
        This is the data-preservation step before the eventual cutover — the app still reads and writes
        Airtable for now. It's idempotent: re-run it any time (and again right before cutover) to sync.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn(false)} disabled={busy} onClick={() => call('GET')}>{busy ? 'Working…' : 'Check counts (dry run)'}</button>
        <button style={btn(true)} disabled={busy} onClick={() => call('POST')}>{busy ? 'Working…' : 'Run import'}</button>
      </div>
      {err && <div style={{ marginTop: 12, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {result.dryRun
            ? `Dry run — Airtable: ${result.airtableRecords} · Supabase: ${result.supabaseRows}`
            : `✓ Imported ${result.imported} · Airtable: ${result.airtableRecords} · Supabase now: ${result.supabaseRows}` +
              (result.airtableRecords === result.supabaseRows ? ' · counts match' : ' · ⚠ counts differ')}
        </div>
      )}
    </div>
  )
}
